/**
 * Settings cards grid component
 * Displays cards for each settings tab when onboarding is complete
 */

import React from "react";
import { Box, Flex, Paper, Stack, Text } from "@mantine/core";
import {
  IconUser,
  IconBriefcase,
  IconUsers,
  IconCreditCard,
  IconPlug,
  IconGift,
  IconLink,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@utils/stores/authStore";
import { isIntakeFrontend } from "@utils/frontendMigration.ts";
import { AppRoutes } from "@/constants";
import { useShallow } from "zustand/shallow";

interface SettingsCard {
  id: string;
  title: string;
  description: string;
  icon: typeof IconUser;
  gradient: string;
  tab: string;
  ownerOnly?: boolean;
  requiresIntakeFrontend?: boolean;
}

const settingsCards: SettingsCard[] = [
  {
    id: "account",
    title: "Account",
    description: "Manage your personal information and password",
    icon: IconUser,
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    tab: "Account",
    ownerOnly: false,
  },
  {
    id: "business",
    title: "Business",
    description: "Update your business information and branding",
    icon: IconBriefcase,
    gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    tab: "Business",
    ownerOnly: true,
  },
  {
    id: "team",
    title: "Team",
    description: "Manage team members and credit allocation",
    icon: IconUsers,
    gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    tab: "Team",
    ownerOnly: false,
  },
  {
    id: "billing",
    title: "Billing",
    description: "View and manage your billing and plan",
    icon: IconCreditCard,
    gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    tab: "Billing",
    ownerOnly: true,
  },
  {
    id: "integrations",
    title: "Integrations",
    description: "Connect with external tools and services",
    icon: IconPlug,
    gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    tab: "Integrations",
    ownerOnly: false,
  },
  {
    id: "referral",
    title: "Referral",
    description: "Share your referral link and earn rewards",
    icon: IconGift,
    gradient: "linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)",
    tab: "Referral",
    ownerOnly: false,
  },
  {
    id: "smart_intake",
    title: "Smart Intake",
    description: "Share your client signup link for document upload",
    icon: IconLink,
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    tab: "Smart_Intake",
    ownerOnly: false,
    requiresIntakeFrontend: true,
  },
];

interface SettingsCardsGridProps {
  onViewOnboardingSteps: () => void;
  isOnboardingCollapsed: boolean;
}

const SettingsCardsGrid: React.FC<SettingsCardsGridProps> = ({
  onViewOnboardingSteps,
  isOnboardingCollapsed,
}) => {
  const navigate = useNavigate();
  const { isOwner, shouldHideBillingSettings, user } = useAuthStore(
    useShallow((state) => ({
      isOwner: state.isOwner,
      shouldHideBillingSettings: state.shouldHideBillingSettings,
      user: state.user,
    }))
  );
  const hideBilling = shouldHideBillingSettings();
  const onIntakeFrontend = isIntakeFrontend(user);

  // Filter cards based on user permissions and frontend mode
  const visibleCards = settingsCards.filter((card) => {
    if (card.ownerOnly && !isOwner()) return false;
    if (card.id === "billing" && hideBilling) return false;
    if (card.requiresIntakeFrontend && !onIntakeFrontend) return false;
    return true;
  });

  const handleCardClick = (tab: string) => {
    // Navigate to lite home with setting query parameter instead of settings page
    const settingId = tab.toLowerCase();
    navigate(`${AppRoutes.homeLite}?setting=${settingId}`);
  };

  return (
    <Stack gap="lg" mt="xl">
      {/* Cards Grid */}
      <Flex wrap="wrap" gap="lg" justify="flex-start">
        {visibleCards.map((card) => {
          const IconComponent = card.icon;

          return (
            <Paper
              key={card.id}
              shadow="lg"
              p="md"
              radius="md"
              style={{
                position: "relative",
                overflow: "hidden",
                backgroundColor: "#ffffff",
                border: "2px solid #e9ecef",
                transition: "all 0.3s ease",
                cursor: "pointer",
                flex: "0 1 calc(25% - 12px)",
                maxWidth: "220px",
                minWidth: "160px",
                aspectRatio: "1",
                display: "flex",
                flexDirection: "column",
              }}
              onClick={() => handleCardClick(card.tab)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform =
                  "translateY(-6px) scale(1.02)";
                e.currentTarget.style.boxShadow =
                  "0 12px 24px rgba(0, 0, 0, 0.15)";
                e.currentTarget.style.borderColor = "#868e96";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.1)";
                e.currentTarget.style.borderColor = "#e9ecef";
              }}
            >
              <Stack gap="sm" style={{ flex: 1 }}>
                {/* Icon */}
                <Box
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: "10px",
                    background: card.gradient,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "transform 0.3s ease",
                  }}
                >
                  <IconComponent size={24} color="white" stroke={1.5} />
                </Box>

                {/* Title */}
                <Text size="xl" fw={700} c="dark" lineClamp={1}>
                  {card.title}
                </Text>

                {/* Description */}
                <Text
                  size="md"
                  c="dimmed"
                  lineClamp={3}
                  style={{ lineHeight: 1.5, opacity: 0.9 }}
                >
                  {card.description}
                </Text>
              </Stack>
            </Paper>
          );
        })}
      </Flex>

      {/* View/Close Onboarding Steps Link */}
      <Box style={{ textAlign: "center" }}>
        <Text
          size="sm"
          c="dimmed"
          style={{
            cursor: "pointer",
            textDecoration: "underline",
            display: "inline-block",
          }}
          onClick={onViewOnboardingSteps}
        >
          {isOnboardingCollapsed
            ? "View Onboarding Steps"
            : "Close Onboarding Steps"}
        </Text>
      </Box>
    </Stack>
  );
};

export default SettingsCardsGrid;
